import {
  Box,
  Flex,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import {
  OnDemandCumulativeReportType,
  ProgrammaticCampaign,
  ReportType,
  ReportTypeLabel,
  DSPProvider,
} from "@intentsify/types";
import { useQuery } from "@tanstack/react-query";
import {
  Actions,
  ButtonGroup,
  FilterAsyncTokenBased,
  FiltersContainer,
  FiltersContainerItemSize,
  Loader,
  RangePicker,
  RequestOnDemandCumulativeCampaignReport,
  ScreenBar,
  ViewContainer,
} from "components";
import { DateTime } from "luxon";
import { useUserAgencyCompany } from "queries/companies/useUserAgencyCompany";
import { useEffect, useState } from "react";
import { FiDownload, FiMoreVertical } from "react-icons/fi";
import { useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import { CampaignDetailsScreenDefinition } from "screens/Campaigns";
import { CampaignEnded } from "shared/components";
import { NoEntitySelected } from "shared/components/NoEntitySelected/NoEntitySelected";
import { Nullable } from "types";
import { useScreen } from "utils";
import { DisplayPerformanceInnovidScreenDefinition } from "./DisplayPerformanceInnovid.definition";
import {
  getProgrammaticCampaign,
  listProgrammaticCampaignMeta,
} from "../displayPerformance.requests";
import { getProgrammaticCampaignDatesBasedOnStatus } from "../displayPerformance.utils";
import {
  AccountsPerformance,
  CampaignEngagement,
  CampaignOption,
  CreativeEngagement,
  defaultFetchDataParams,
  DisplayPerformanceFilters,
  DisplayPerformanceView,
  listCampaigns,
} from "../DisplayPerformanceShared";
import { innovidRequests } from "./requests";
import { byLook } from "theme/look";

type DisplayPerformanceContentProps = {
  companyId: number;
  innovidCampaignId: string | undefined;
  view: string | undefined;
};

const DisplayPerformanceContent = ({
  companyId,
  innovidCampaignId,
  view,
}: DisplayPerformanceContentProps) => {
  const navigate = useNavigate();

  const [filters, setFilters] = useState<
    DisplayPerformanceFilters | undefined
  >();
  const [selectedCampaignOption, setSelectedCampaignOption] =
    useState<CampaignOption>();
  const [selectedCampaign, setSelectedCampaign] =
    useState<Nullable<ProgrammaticCampaign>>(null);

  const selectedView =
    (view as DisplayPerformanceView | undefined) ||
    DisplayPerformanceView.Accounts;

  const [onDemandCumulativeReportType, setOnDemandCumulativeReportType] =
    useState<OnDemandCumulativeReportType | undefined>(undefined);

  const topbarColor = useColorModeValue("white", "brand.375");
  const colorGrey = useColorModeValue("gray.25", "brand.375");

  const requestOnDemandCumulativeCampaignReportModalDisclosure =
    useDisclosure();

  const handleChange = (c: CampaignOption | undefined) => {
    if (
      selectedCampaignOption?.value &&
      c?.value === selectedCampaignOption.value
    ) {
      return;
    }

    if (c?.value !== innovidCampaignId) {
      setFilters(undefined);
      setSelectedCampaign(null);
    }

    if (!c) {
      setSelectedCampaignOption(undefined);
      setSelectedCampaign(null);
      navigate(
        DisplayPerformanceInnovidScreenDefinition.navigate({
          innovidCampaignId: undefined,
          view: undefined,
        })
      );
    } else {
      setFilters(undefined);
      setSelectedCampaignOption(c);
      navigate(
        DisplayPerformanceInnovidScreenDefinition.navigate({
          innovidCampaignId: c.value,
          view: selectedView,
        })
      );
    }
  };

  const { isFetching: isFetchingCampaign, data: campaignData } = useQuery(
    [`programmatic-campaign`, innovidCampaignId, companyId],
    () => getProgrammaticCampaign(String(innovidCampaignId)),
    {
      enabled: !!innovidCampaignId,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
    }
  );

  // Sync state with query data (cached or fetched)
  useEffect(() => {
    if (campaignData) {
      setSelectedCampaignOption({
        value: campaignData.id,
        label: campaignData.displayName,
        companyId: campaignData.companyId,
      });
      setSelectedCampaign(campaignData);
      setFilters((prevState) => {
        const dates = getProgrammaticCampaignDatesBasedOnStatus({
          weekTo: campaignData.weekTo,
          yearTo: campaignData.yearTo,
          weekFrom: campaignData.weekFrom,
          yearFrom: campaignData.yearFrom,
          isEnded: campaignData.isEnded,
        });
        return prevState ? { ...prevState, ...dates } : dates;
      });
    }
  }, [campaignData]);

  const { data: programmaticCampaignMeta } = useQuery(
    [`programmatic-campaign-campaigns`, innovidCampaignId, companyId],
    () => listProgrammaticCampaignMeta(String(innovidCampaignId)),
    {
      enabled: !!innovidCampaignId,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
    }
  );

  const openRequestOnDemandCumulativeCampaignReportModal = (
    reportType: OnDemandCumulativeReportType
  ) => {
    setOnDemandCumulativeReportType(reportType);
    requestOnDemandCumulativeCampaignReportModalDisclosure.onOpen();
  };

  const title = !innovidCampaignId
    ? "Overall Digital Performance Reporting"
    : selectedCampaignOption?.label || "";

  return (
    <ViewContainer noPadding withBackground stretch>
      <Flex width="full" backgroundColor={topbarColor}>
        <Box maxWidth={byLook(undefined, "1640px")} width="full">
          <ScreenBar
            filters={[
              {
                width: "400",
                node: (
                  <Flex pl={1}>
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="bold">{title}</Text>

                      {innovidCampaignId && selectedCampaign?.isEnded && (
                        <CampaignEnded />
                      )}

                      {selectedCampaign && programmaticCampaignMeta && (
                        <>
                          {programmaticCampaignMeta.campaigns?.length > 0 && (
                            <>
                              <Text fontSize="xs" color="gray.500">
                                Associated intent models:
                              </Text>
                              {programmaticCampaignMeta.campaigns.map(
                                (campaign) => (
                                  <Text
                                    key={campaign.id}
                                    as={Link}
                                    fontSize="xs"
                                    color="gray.500"
                                    textDecoration="underline"
                                    cursor="pointer"
                                    textAlign="left"
                                    to={CampaignDetailsScreenDefinition.navigate(
                                      {
                                        campaignId: campaign.id,
                                        view: "weekly-snapshot",
                                      }
                                    )}
                                  >
                                    {campaign.id} - {campaign.displayName}
                                  </Text>
                                )
                              )}
                            </>
                          )}
                        </>
                      )}
                    </VStack>
                  </Flex>
                ),
              },
              {
                label: "Campaigns",
                width: "500",
                node: (
                  <Box
                    flexGrow={1}
                    display="flex"
                    data-testid="campaign-selector"
                    alignItems="center"
                    gap={2}
                  >
                    <Box width={"80%"}>
                      <FilterAsyncTokenBased
                        isMulti={false}
                        currentValue={selectedCampaignOption}
                        defaultOptions={[]}
                        dataRequest={listCampaigns([DSPProvider.innovid])}
                        placeholder="Select a campaign"
                        onFilterValuesChange={handleChange}
                        params={defaultFetchDataParams}
                        noDataTooltip="There are currently no Innovid campaigns associated with your programs"
                      />
                    </Box>
                    <Flex gap={2} alignItems="center">
                      {onDemandCumulativeReportType && innovidCampaignId && (
                        <RequestOnDemandCumulativeCampaignReport
                          key={`${String(
                            innovidCampaignId
                          )}_${onDemandCumulativeReportType}`}
                          campaignId={innovidCampaignId}
                          campaignStartDate={DateTime.fromObject({
                            weekNumber: selectedCampaign?.weekFrom,
                            weekYear: selectedCampaign?.yearFrom,
                          })
                            .startOf("week")
                            .toISO()}
                          campaignEndDate={DateTime.fromObject({
                            weekNumber: selectedCampaign?.weekTo,
                            weekYear: selectedCampaign?.yearTo,
                          })
                            .endOf("week")
                            .toISO()}
                          isOpen={
                            requestOnDemandCumulativeCampaignReportModalDisclosure.isOpen
                          }
                          onClose={
                            requestOnDemandCumulativeCampaignReportModalDisclosure.onClose
                          }
                          reportType={onDemandCumulativeReportType}
                          firstImpressionDate={
                            selectedCampaign?.firstImpressionDate
                          }
                        />
                      )}
                    </Flex>

                    {selectedCampaign && (
                      <Actions
                        items={[
                          {
                            label: `Export ${
                              ReportTypeLabel[ReportType.InnovidAccountLevel]
                            }`,
                            onClick: () =>
                              openRequestOnDemandCumulativeCampaignReportModal(
                                ReportType.InnovidAccountLevel
                              ),
                            icon: <FiDownload />,
                          },
                        ]}
                        numberOfVisibleItems={0}
                        iconsOnly={false}
                        buttonIcon={<FiMoreVertical />}
                        buttonsVariant="outline-teal"
                      />
                    )}
                  </Box>
                ),
              },
            ]}
            justifyContent="space-between"
          />
        </Box>
      </Flex>
      <Flex
        flexWrap="wrap"
        borderTop={2}
        borderTopStyle={"solid"}
        borderTopColor={colorGrey}
      >
        <Box
          paddingX={2}
          backgroundColor={topbarColor}
          flexBasis="100%"
          minHeight="56px"
        >
          {innovidCampaignId && filters && (
            <FiltersContainer
              items={[
                {
                  component: (
                    <Stack>
                      <Text fontSize="xs" fontWeight="semibold">
                        Date range:
                      </Text>
                      <RangePicker
                        minW="100%"
                        isDisabled={isFetchingCampaign}
                        placement="bottom-start"
                        mode="week"
                        selected={{
                          start: DateTime.fromObject({
                            weekNumber: filters.weekFrom,
                            weekYear: filters.yearFrom,
                          }).toISO(),
                          end: DateTime.fromObject({
                            weekNumber: filters.weekTo,
                            weekYear: filters.yearTo,
                          }).toISO(),
                        }}
                        onChange={(val) => {
                          const start = DateTime.fromISO(val.start);
                          const end = DateTime.fromISO(val.end);

                          setFilters((prev) => {
                            return {
                              ...prev,
                              weekFrom: start.weekNumber,
                              yearFrom: start.year,
                              weekTo: end.weekNumber,
                              yearTo: end.year,
                            };
                          });
                        }}
                        disabledDays={[
                          {
                            lt: DateTime.fromObject({
                              weekNumber: selectedCampaign?.weekFrom,
                              weekYear: selectedCampaign?.yearFrom,
                            }).startOf("week"),
                          },
                          {
                            gt: DateTime.fromObject({
                              weekNumber: selectedCampaign?.weekTo,
                              weekYear: selectedCampaign?.yearTo,
                            }).endOf("week"),
                          },
                        ]}
                      />
                    </Stack>
                  ),
                },
                {
                  size: FiltersContainerItemSize.XL,
                  component: (
                    <Stack w="280px">
                      <Text fontSize="xs" fontWeight="semibold">
                        Engagement by:
                      </Text>
                      <ButtonGroup
                        selectedValue={selectedView}
                        setSelectedValue={(view) => {
                          navigate(
                            DisplayPerformanceInnovidScreenDefinition.navigate({
                              innovidCampaignId: innovidCampaignId,
                              view,
                            })
                          );
                        }}
                        items={[
                          {
                            label: "Accounts",
                            value: DisplayPerformanceView.Accounts,
                          },
                          {
                            label: "Campaign",
                            value: DisplayPerformanceView.CampaignEngagement,
                          },
                          {
                            label: "Creative",
                            value: DisplayPerformanceView.CreativeEngagement,
                          },
                        ]}
                      />
                    </Stack>
                  ),
                },
              ]}
              withMarginBottom={false}
            />
          )}
        </Box>
        <Box width={"full"} maxWidth={byLook(undefined, "1640px")}>
          {!innovidCampaignId ? (
            <NoEntitySelected entityName="campaign" />
          ) : (
            <>
              {!filters ? (
                <Loader />
              ) : (
                <ViewContainer>
                  {selectedView ===
                    DisplayPerformanceView.CampaignEngagement && (
                    <CampaignEngagement
                      campaignName={title}
                      filters={filters}
                      id={innovidCampaignId}
                      requests={innovidRequests.campaignEngagement}
                      isBasicView
                    />
                  )}
                  {selectedView === DisplayPerformanceView.Accounts && (
                    <AccountsPerformance
                      campaignName={title}
                      filters={filters}
                      id={innovidCampaignId}
                      companyId={selectedCampaignOption?.companyId}
                      requests={innovidRequests.accountsPerformance}
                      isBasicView
                    />
                  )}
                  {selectedView ===
                    DisplayPerformanceView.CreativeEngagement && (
                    <CreativeEngagement
                      filters={filters}
                      id={innovidCampaignId}
                      requests={innovidRequests.creativeEngagement}
                      isBasicView
                    />
                  )}
                </ViewContainer>
              )}
            </>
          )}
        </Box>
      </Flex>
    </ViewContainer>
  );
};

const DisplayPerformanceInnovid = () => {
  useScreen(DisplayPerformanceInnovidScreenDefinition);
  const { innovidCampaignId, view } = useParams<"innovidCampaignId" | "view">();

  const userAgencyCompany = useUserAgencyCompany();
  const companyId = userAgencyCompany.data?.companyId;

  // Don't render content until company is loaded
  if (!companyId) {
    return <Loader />;
  }

  return (
    <DisplayPerformanceContent
      key={companyId}
      companyId={companyId}
      innovidCampaignId={innovidCampaignId}
      view={view}
    />
  );
};

export { DisplayPerformanceInnovid };
